import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Building2, Pencil, Trash2, Plus, Calendar, TrendingUp, Users, Globe, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';
import TypsaOrgUnitDialog from '../components/TypsaOrgUnitDialog';
import ContactDialog from '../components/ContactDialog';
import ContactCard from '../components/ContactCard';
import OpportunityDialog from '../components/OpportunityDialog';
import ActivityDialog from '../components/ActivityDialog';
import TaskDialog from '../components/TaskDialog';
import OpportunityViewDialog from '../components/OpportunityViewDialog';
import AccountHistorySection from '../components/AccountHistorySection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category labels
const CATEGORY_LABELS = {
  'division': 'División',
  'departamento': 'Departamento',
  'territorio': 'Territorio',
  'grupo': 'Grupo'
};

// Stage colors for opportunity badges
const stageColors = {
  'Detectada': 'bg-blue-100 text-blue-800',
  'Propuesta': 'bg-yellow-100 text-yellow-800',
  'Presentada': 'bg-purple-100 text-purple-800',
  'Ganada': 'bg-green-100 text-green-800',
  'Perdida': 'bg-red-100 text-red-800',
  'Descartada': 'bg-gray-100 text-gray-800',
};

const TypsaOrgUnitDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isEditor, currentUser } = useUser();
  const [unit, setUnit] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [activities, setActivities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [typsaCompanyId, setTypsaCompanyId] = useState(null);
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [opportunityDialogOpen, setOpportunityDialogOpen] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  
  const [editingContact, setEditingContact] = useState(null);
  const [editingOpportunity, setEditingOpportunity] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  
  const [deleteContactConfirm, setDeleteContactConfirm] = useState(null);
  const [deleteOpportunityConfirm, setDeleteOpportunityConfirm] = useState(null);
  const [deleteActivityConfirm, setDeleteActivityConfirm] = useState(null);
  const [deleteTaskConfirm, setDeleteTaskConfirm] = useState(null);
  
  const [viewingOpportunity, setViewingOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      // Get the org unit
      const unitRes = await axios.get(`${API}/typsa-org-units/${id}`);
      setUnit(unitRes.data);
      
      // Get TYPSA company ID
      const companiesRes = await axios.get(`${API}/companies`);
      const typsaCompany = companiesRes.data.find(c => 
        c.name?.toUpperCase().includes('TYPSA') || c.company_type === 'typsa'
      );
      if (typsaCompany) {
        setTypsaCompanyId(typsaCompany.id);
      }
      
      // Get all contacts linked to this org unit
      const contactsRes = await axios.get(`${API}/contacts?include_org_unit=true`);
      const unitContacts = contactsRes.data.filter(c => c.typsa_org_unit_id === id);
      setContacts(unitContacts);
      
      // Get contact IDs for filtering
      const contactIds = unitContacts.map(c => c.id);
      
      // Get all related data
      const [oppsRes, activitiesRes, tasksRes] = await Promise.all([
        axios.get(`${API}/opportunities`),
        axios.get(`${API}/activities`),
        axios.get(`${API}/tasks`)
      ]);
      
      // Filter data by org unit (via typsa_org_unit_id or contact_id)
      const filteredOpps = oppsRes.data.filter(o => 
        o.typsa_org_unit_id === id || contactIds.includes(o.contact_id)
      );
      setOpportunities(filteredOpps);
      
      const filteredActivities = activitiesRes.data.filter(a => 
        a.typsa_org_unit_id === id || 
        (a.contact_ids && a.contact_ids.some(cId => contactIds.includes(cId))) ||
        contactIds.includes(a.contact_id)
      );
      setActivities(filteredActivities);
      
      const filteredTasks = tasksRes.data.filter(t => 
        t.typsa_org_unit_id === id || contactIds.includes(t.contact_id)
      );
      setTasks(filteredTasks);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/typsa-org-units/${id}`, {
        headers: { 'x-user-email': currentUser?.email }
      });
      toast.success('Unidad organizativa eliminada');
      navigate('/companies');
    } catch (error) {
      console.error('Error deleting unit:', error);
      toast.error('Error al eliminar');
    }
  };

  const handleDeleteContact = async () => {
    if (!deleteContactConfirm) return;
    try {
      await axios.delete(`${API}/contacts/${deleteContactConfirm.id}`, {
        headers: { 'x-user-email': currentUser?.email }
      });
      toast.success('Contacto eliminado');
      setDeleteContactConfirm(null);
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar');
    }
  };

  const handleDeleteOpportunity = async () => {
    if (!deleteOpportunityConfirm) return;
    try {
      await axios.delete(`${API}/opportunities/${deleteOpportunityConfirm.id}`, {
        headers: { 'x-user-email': currentUser?.email }
      });
      toast.success('Oportunidad eliminada');
      setDeleteOpportunityConfirm(null);
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar');
    }
  };

  const handleDeleteActivity = async () => {
    if (!deleteActivityConfirm) return;
    try {
      await axios.delete(`${API}/activities/${deleteActivityConfirm.id}`, {
        headers: { 'x-user-email': currentUser?.email }
      });
      toast.success('Actividad eliminada');
      setDeleteActivityConfirm(null);
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar');
    }
  };

  const handleDeleteTask = async () => {
    if (!deleteTaskConfirm) return;
    try {
      await axios.delete(`${API}/tasks/${deleteTaskConfirm.id}`, {
        headers: { 'x-user-email': currentUser?.email }
      });
      toast.success('Tarea eliminada');
      setDeleteTaskConfirm(null);
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  if (!unit) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="text-muted-foreground mb-4">Unidad organizativa no encontrada</div>
        <Button onClick={() => navigate('/companies')}>Volver a Empresas</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="typsa-org-unit-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/companies')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-500 text-white">
                {CATEGORY_LABELS[unit.category] || unit.category}
              </Badge>
              <h1 className="text-3xl font-bold tracking-tight">{unit.value}</h1>
            </div>
            <p className="text-muted-foreground mt-1">Organización TYPSA</p>
          </div>
        </div>
        {isEditor && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(true)}>
              <Pencil className="h-4 w-4 mr-2" />
              Editar
            </Button>
            <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="general" className="space-y-4">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="contacts">
            Contactos ({contacts.length})
          </TabsTrigger>
          <TabsTrigger value="opportunities">
            Oport. ({opportunities.length})
          </TabsTrigger>
          <TabsTrigger value="activities">
            Actividades ({activities.length})
          </TabsTrigger>
          <TabsTrigger value="tasks">
            Tareas ({tasks.length})
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>Información de la Unidad</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {unit.website && (
                  <div>
                    <div className="text-sm text-muted-foreground uppercase tracking-wider mb-1">Sitio Web</div>
                    <a 
                      href={unit.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline flex items-center gap-1"
                    >
                      <Globe className="w-4 h-4" />
                      {unit.website}
                    </a>
                  </div>
                )}
                <div>
                  <div className="text-sm text-muted-foreground uppercase tracking-wider mb-1">Responsable</div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span>{unit.owner || 'Sin asignar'}</span>
                  </div>
                </div>
                {unit.description && (
                  <div className="md:col-span-2">
                    <div className="text-sm text-muted-foreground uppercase tracking-wider mb-1">Descripción</div>
                    <p>{unit.description}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Account History Section */}
          <AccountHistorySection typsaOrgUnitId={id} />
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Contactos
              </CardTitle>
              {isEditor && (
                <Button size="sm" onClick={() => { setEditingContact(null); setContactDialogOpen(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo Contacto
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {contacts.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No hay contactos en esta unidad</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {contacts.map((contact) => (
                    <ContactCard
                      key={contact.id}
                      contact={contact}
                      companies={[]}
                      typsaOrgUnits={[unit]}
                      isEditor={isEditor}
                      onEdit={(c) => { setEditingContact(c); setContactDialogOpen(true); }}
                      onDelete={(c) => setDeleteContactConfirm(c)}
                      showCompanyName={false}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Opportunities Tab */}
        <TabsContent value="opportunities">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Oportunidades
              </CardTitle>
              {isEditor && (
                <Button size="sm" onClick={() => { setEditingOpportunity(null); setOpportunityDialogOpen(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Oportunidad
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {opportunities.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No hay oportunidades en esta unidad</p>
              ) : (
                <div className="space-y-3">
                  {opportunities.map((opp) => (
                    <div
                      key={opp.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                      onClick={() => setViewingOpportunity(opp)}
                    >
                      <div>
                        <div className="font-medium">{opp.title || opp.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {opp.estimated_value ? `€${opp.estimated_value.toLocaleString()}` : 'Sin valor'}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={stageColors[opp.stage] || 'bg-gray-100'}>
                          {opp.stage}
                        </Badge>
                        {isEditor && (
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); setEditingOpportunity(opp); setOpportunityDialogOpen(true); }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); setDeleteOpportunityConfirm(opp); }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activities Tab */}
        <TabsContent value="activities">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Actividades
              </CardTitle>
              {isEditor && (
                <Button size="sm" onClick={() => { setEditingActivity(null); setActivityDialogOpen(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Actividad
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {activities.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No hay actividades en esta unidad</p>
              ) : (
                <div className="space-y-3">
                  {activities.map((activity) => (
                    <div
                      key={activity.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                    >
                      <div>
                        <div className="font-medium">{activity.title}</div>
                        <div className="text-sm text-muted-foreground">{activity.date}</div>
                        <div className="text-sm text-muted-foreground capitalize">{activity.type}</div>
                      </div>
                      {isEditor && (
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => { setEditingActivity(activity); setActivityDialogOpen(true); }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setDeleteActivityConfirm(activity)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Tareas</CardTitle>
              {isEditor && (
                <Button size="sm" onClick={() => { setEditingTask(null); setTaskDialogOpen(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Tarea
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {tasks.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No hay tareas en esta unidad</p>
              ) : (
                <div className="space-y-3">
                  {tasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                    >
                      <div>
                        <div className="font-medium">{task.title}</div>
                        <div className="text-sm text-muted-foreground">{task.due_date}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={task.status === 'Completed' ? 'default' : 'secondary'}>
                          {task.status === 'Completed' ? 'Completada' : 'Pendiente'}
                        </Badge>
                        {isEditor && (
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => { setEditingTask(task); setTaskDialogOpen(true); }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setDeleteTaskConfirm(task)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <TypsaOrgUnitDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        unit={unit}
        onSuccess={fetchData}
      />

      <ContactDialog
        open={contactDialogOpen}
        onOpenChange={setContactDialogOpen}
        companyId={typsaCompanyId}
        contact={editingContact}
        onSuccess={fetchData}
      />

      <OpportunityDialog
        open={opportunityDialogOpen}
        onOpenChange={setOpportunityDialogOpen}
        companyId={typsaCompanyId}
        opportunity={editingOpportunity}
        onSuccess={fetchData}
      />

      <ActivityDialog
        open={activityDialogOpen}
        onOpenChange={setActivityDialogOpen}
        companyId={typsaCompanyId}
        activity={editingActivity}
        onSuccess={fetchData}
      />

      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        companyId={typsaCompanyId}
        task={editingTask}
        onSuccess={fetchData}
      />

      {viewingOpportunity && (
        <OpportunityViewDialog
          open={!!viewingOpportunity}
          onOpenChange={(open) => !open && setViewingOpportunity(null)}
          opportunity={viewingOpportunity}
        />
      )}

      {/* Delete Confirmations */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar unidad organizativa?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente la unidad "{unit.value}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteContactConfirm} onOpenChange={() => setDeleteContactConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar contacto?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteContact} className="bg-destructive text-destructive-foreground">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteOpportunityConfirm} onOpenChange={() => setDeleteOpportunityConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar oportunidad?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteOpportunity} className="bg-destructive text-destructive-foreground">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteActivityConfirm} onOpenChange={() => setDeleteActivityConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar actividad?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteActivity} className="bg-destructive text-destructive-foreground">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTaskConfirm} onOpenChange={() => setDeleteTaskConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar tarea?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTask} className="bg-destructive text-destructive-foreground">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TypsaOrgUnitDetail;
